<template>
  <div class="page-wrapper">
    <div class="kb-container">
      <!-- 顶部操作区 -->
      <div class="search-header">
        <div class="left">
          <el-input
            v-model="searchKey"
            placeholder="搜索我的知识库..."
            class="search-input"
            prefix-icon="el-icon-search"
            @input="handleSearch"
          />
        </div>
        
        <div class="right">
          <el-button type="primary" v-real-name-auth="'创建知识库'" @click="createKnowledgeBase">
            <i class="el-icon-plus"></i> 创建知识库
          </el-button>
        </div>
      </div>

      <!-- 标签页切换 -->
      <el-tabs v-model="activeTab" @tab-click="handleTabClick">
        <el-tab-pane label="我创建的" name="created"></el-tab-pane>
        <el-tab-pane label="我协作的" name="collaborated"></el-tab-pane>
        <el-tab-pane label="我收藏的" name="favorited"></el-tab-pane>
      </el-tabs>

      <!-- 知识库列表 -->
      <div class="kb-list" v-loading="loading">
        <el-empty v-if="knowledgeBases.length === 0" description="暂无数据"></el-empty>
        <el-row :gutter="24" v-else>
          <el-col :xs="24" :sm="12" :md="8" :lg="8" v-for="(item, index) in knowledgeBases" :key="index">
            <div class="kb-card">
              <!-- 封面区域 -->
              <div class="kb-cover" :style="{ backgroundImage: `url(${item?.coverImage })` }">
                <div class="kb-type" :class="getAccessTypeClass(item.accessType)">
                  {{ getAccessTypeName(item.accessType) }}
                </div>
              </div>
              
              <!-- 内容区域 -->
              <div class="kb-content">
                <div class="kb-header">
                  <h3 class="kb-title" @click="viewKnowledgeBase(item.id)">{{ item.name }}</h3>
                  <el-dropdown trigger="click" @command="(command) => handleCommand(command, item)">
                    <i class="el-icon-more"></i>
                    <el-dropdown-menu slot="dropdown">
                      <el-dropdown-item command="view">查看</el-dropdown-item>
                      <el-dropdown-item v-if="activeTab === 'created'" command="edit">编辑</el-dropdown-item>
                      <el-dropdown-item v-if="activeTab === 'created'" command="delete">删除</el-dropdown-item>
                      <el-dropdown-item v-if="activeTab === 'favorited'" command="unfavorite">取消收藏</el-dropdown-item>
                    </el-dropdown-menu>
                  </el-dropdown>
                </div>
                
                <p class="kb-description">{{ item.description || '暂无描述' }}</p>
                
                <div class="kb-meta">
                  <div class="kb-stats">
                    <span><i class="el-icon-view"></i> {{ item.viewCount || 0 }}</span>
                    <span><i class="el-icon-star-off"></i> {{ item.favoriteCount || 0 }}</span>
                    <span><i class="el-icon-document"></i> {{ item.documentCount || 0 }}</span>
                  </div>
                </div>
              </div>
            </div>
          </el-col>
        </el-row>
      </div>
      
      <!-- 分页 -->
      <div class="pagination-container" v-if="total > 0">
        <el-pagination
          background
          layout="prev, pager, next"
          :total="total"
          :page-size="pageSize"
          :current-page.sync="currentPage"
          @current-change="handlePageChange"
        ></el-pagination>
      </div>
    </div>
    
    <!-- 删除确认对话框 -->
    <el-dialog
      title="确认删除"
      :visible.sync="deleteDialogVisible"
      width="400px"
    >
      <p>确定要删除知识库 "{{ currentKnowledgeBase?.name }}" 吗？此操作不可恢复！</p>
      <div slot="footer" class="dialog-footer">
        <el-button @click="deleteDialogVisible = false">取消</el-button>
        <el-button type="danger" @click="confirmDelete">确定删除</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { listKnowledgeBase, delKnowledgeBase } from '@/api/knowledgeBase/knowledgeBase';
import { getFavoriteList, toggleFavorite } from '@/api/knowledgeBase/favorite';
import {listCollaborators} from '@/api/knowledgeBase/collaborator'
export default {
  name: 'MyKnowledgeBase',
  data() {
    return {
      // 搜索关键字
      searchKey: '',
      // 知识库列表
      knowledgeBases: [],
      // 分页
      currentPage: 1,
      pageSize: 9,
      total: 0,
      // 加载状态
      loading: false,
      // 当前选中的知识库（用于删除确认）
      currentKnowledgeBase: null,
      // 删除对话框
      deleteDialogVisible: false,
      // 当前激活的标签页
      activeTab: 'created'
    };
  },
  created() {
    this.loadKnowledgeBases();
  },
  methods: {
    // 加载知识库列表
    loadKnowledgeBases() {
      this.loading = true;
      
      const params = {
        pageNum: this.currentPage,
        pageSize: this.pageSize,
        name: this.searchKey
      };
      
      let request;
      
      if (this.activeTab === 'created') {
        // 我创建的
        params.createBy =this.userStore.userInfo.id;
        request = listKnowledgeBase(params);
      } else if (this.activeTab === 'collaborated') {
        // 我协作的
        params.userId =this.userStore.userInfo.id;
        request = listCollaborators(params);
      } else if (this.activeTab === 'favorited') {
        // 我收藏的
        request = getFavoriteList(params);
      }
      
      request.then(response => {
        if (response.code === 200) {
          if (this.activeTab === 'favorited'||this.activeTab=='collaborated') {
            // 收藏列表需要特殊处理，因为返回的是收藏记录
            this.knowledgeBases = response.rows.filter((item) => item.knowledgeBase!= null).map(item => item.knowledgeBase);
          } else {
            this.knowledgeBases = response.rows;
          }
          this.total = response.total;
        } else {
          this.$message.error(response.msg || '获取知识库列表失败');
        }
        this.loading = false;
      }).catch(error => {
        console.error('获取知识库列表失败', error);
        this.$message.error('获取知识库列表失败，请稍后重试');
        this.loading = false;
      });
    },
    
    // 处理搜索
    handleSearch() {
      this.currentPage = 1;
      this.loadKnowledgeBases();
    },
    
    // 处理页码变化
    handlePageChange(page) {
      this.currentPage = page;
      this.loadKnowledgeBases();
    },
    
    // 处理标签页切换
    handleTabClick() {
      this.currentPage = 1;
      this.loadKnowledgeBases();
    },
    
    // 创建知识库
    createKnowledgeBase() {
      this.$router.push('/knowledgeBase/create');
    },
    
    // 查看知识库
    viewKnowledgeBase(id) {
      this.$router.push(`/knowledgeBase/detail/${id}`);
    },
    
    // 处理下拉菜单命令
    handleCommand(command, item) {
      if (command === 'view') {
        this.viewKnowledgeBase(item.id);
      } else if (command === 'edit') {
        this.$router.push(`/knowledgeBase/edit/${item.id}`);
      } else if (command === 'delete') {
        this.currentKnowledgeBase = item;
        this.deleteDialogVisible = true;
      } else if (command === 'unfavorite') {
        this.unfavoriteKnowledgeBase(item);
      }
    },
    
    // 确认删除
    confirmDelete() {
      if (!this.currentKnowledgeBase) return;
      
      delKnowledgeBase(this.currentKnowledgeBase.id).then(response => {
        if (response.code === 200) {
          this.$message.success('删除成功');
          this.deleteDialogVisible = false;
          this.loadKnowledgeBases();
        } else {
          this.$message.error(response.msg || '删除失败');
        }
      }).catch(error => {
        console.error('删除失败', error);
        this.$message.error('删除失败，请稍后重试');
      });
    },
    
    // 取消收藏
    unfavoriteKnowledgeBase(item) {
      toggleFavorite(item.id).then(response => {
        if (response.code === 200) {
          this.$message.success('取消收藏成功');
          this.loadKnowledgeBases();
        } else {
          this.$message.error(response.msg || '取消收藏失败');
        }
      }).catch(error => {
        console.error('取消收藏失败', error);
        this.$message.error('取消收藏失败，请稍后重试');
      });
    },
    
    // 获取访问类型样式类
    getAccessTypeClass(type) {
      switch (type) {
        case 0: return 'public';
        case 1: return 'private';
        case 2: return 'paid';
        default: return 'public';
      }
    },
    
    // 获取访问类型名称
    getAccessTypeName(type) {
      switch (type) {
        case 0: return '公开';
        case 1: return '私有';
        case 2: return '付费';
        default: return '公开';
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.page-wrapper {
  background-color: #f5f7fa;
  width: 100%;
}

.kb-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 24px;
}

.search-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  
  .left {
    flex: 1;
    max-width: 400px;
  }
  
  .right {
    margin-left: 16px;
  }
}

.kb-list {
  margin-top: 24px;
}

.kb-card {
  background-color: #fff;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
  transition: transform 0.3s ease;
  margin-bottom: 24px;
  
  &:hover {
    transform: translateY(-5px);
  }
}

.kb-cover {
  height: 150px;
  background-size: cover;
  background-position: center;
  position: relative;
  
  .kb-type {
    position: absolute;
    top: 12px;
    right: 12px;
    padding: 4px 8px;
    border-radius: 4px;
    font-size: 12px;
    color: #fff;
    
    &.public {
      background-color: #67C23A;
    }
    
    &.private {
      background-color: #909399;
    }
    
    &.paid {
      background-color: #E6A23C;
    }
  }
}

.kb-content {
  padding: 16px;
}

.kb-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 12px;
  
  .kb-title {
    margin: 0;
    font-size: 18px;
    font-weight: 600;
    color: #303133;
    cursor: pointer;
    
    &:hover {
      color: #409EFF;
    }
  }
  
  .el-icon-more {
    font-size: 20px;
    color: #909399;
    cursor: pointer;
    
    &:hover {
      color: #606266;
    }
  }
}

.kb-description {
  margin: 0 0 16px;
  font-size: 14px;
  color: #606266;
  line-height: 1.5;
  height: 42px;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

.kb-meta {
  display: flex;
  justify-content: space-between;
  align-items: center;
  
  .kb-creator {
    display: flex;
    align-items: center;
    
    span {
      margin-left: 8px;
      font-size: 14px;
      color: #606266;
    }
  }
  
  .kb-stats {
    display: flex;
    
    span {
      margin-left: 12px;
      font-size: 12px;
      color: #909399;
      
      i {
        margin-right: 4px;
      }
    }
  }
}

.pagination-container {
  margin-top: 24px;
  text-align: center;
}

// 响应式布局
@media screen and (max-width: 768px) {
  .kb-container {
    padding: 16px;
  }
  
  .search-header {
    flex-direction: column;
    
    .left {
      width: 100%;
      max-width: none;
    }
    
    .right {
      margin-left: 0;
      margin-top: 16px;
      width: 100%;
      
      .el-button {
        width: 100%;
      }
    }
  }
}
</style> 